<template>
    <div class="wf-field">
        <label class="wf-label">
            {{ label }} <span v-if="isRequired" class="wf-required">*</span>
        </label>
        <p v-if="instruction" class="wf-instruction">{{ instruction }}</p>

        <div class="wf-audio">
            <div v-if="!recording && !audioUrl" class="wf-audio-idle">
                <button type="button" class="wf-audio-record-btn" @click="startRecording">
                    <i class="fal fa-microphone"></i>
                </button>
                <span class="wf-audio-hint">{{ $t('wizard.audio_click_to_record') }}</span>
            </div>

            <div v-if="recording" class="wf-audio-recording">
                <div class="wf-audio-pulse"></div>
                <span class="wf-audio-timer">{{ formattedTime }}</span>
                <button type="button" class="wf-audio-stop-btn" @click="stopRecording">
                    <i class="fal fa-stop"></i>
                </button>
            </div>

            <div v-if="audioUrl && !recording" class="wf-audio-preview">
                <audio :src="audioUrl" controls class="wf-audio-player"></audio>
                <button type="button" class="wf-audio-redo" @click="reset">
                    <i class="fal fa-redo mr-1"></i> {{ $t('wizard.audio_redo') }}
                </button>
            </div>
        </div>

        <p v-if="help" class="wf-help">{{ help }}</p>
    </div>
</template>

<script>
import FieldMixin from './FieldMixin'

export default {
    name: 'AudioField',
    mixins: [FieldMixin],
    data() {
        return {
            recording: false,
            mediaRecorder: null,
            chunks: [],
            audioUrl: null,
            audioBlob: null,
            elapsed: 0,
            timer: null
        }
    },
    computed: {
        maxDuration() {
            return (this.attribute.config && this.attribute.config.maxDuration) || 60
        },
        formattedTime() {
            const m = Math.floor(this.elapsed / 60)
            const s = this.elapsed % 60
            return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
        }
    },
    beforeDestroy() {
        this.cleanup()
    },
    methods: {
        async startRecording() {
            try {
                const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
                this.mediaRecorder = new MediaRecorder(stream)
                this.chunks = []

                this.mediaRecorder.ondataavailable = (e) => {
                    if (e.data.size > 0) this.chunks.push(e.data)
                }

                this.mediaRecorder.onstop = () => {
                    this.audioBlob = new Blob(this.chunks, { type: 'audio/webm' })
                    this.audioUrl = URL.createObjectURL(this.audioBlob)
                    this.blobToBase64(this.audioBlob).then((base64) => {
                        this.emitValue([{
                            name: 'audio-recording.webm',
                            encodedFile: base64
                        }])
                    })
                    stream.getTracks().forEach((t) => t.stop())
                }

                this.mediaRecorder.start()
                this.recording = true
                this.elapsed = 0
                this.timer = setInterval(() => {
                    this.elapsed++
                    if (this.elapsed >= this.maxDuration) this.stopRecording()
                }, 1000)
            } catch (e) {
                console.error('Microphone access denied', e)
            }
        },
        stopRecording() {
            if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
                this.mediaRecorder.stop()
            }
            this.recording = false
            clearInterval(this.timer)
        },
        reset() {
            this.cleanup()
            this.audioUrl = null
            this.audioBlob = null
            this.elapsed = 0
            this.emitValue(null)
        },
        cleanup() {
            clearInterval(this.timer)
            if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
                this.mediaRecorder.stop()
            }
            if (this.audioUrl) URL.revokeObjectURL(this.audioUrl)
        },
        blobToBase64(blob) {
            return new Promise((resolve) => {
                const reader = new FileReader()
                reader.onloadend = () => resolve(reader.result)
                reader.readAsDataURL(blob)
            })
        }
    }
}
</script>
